import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { environment } from 'src/environments/environment';
import { SnackbarService } from '../snackbar/snackbar.service';

const USER_DOCUMENTS_PATH = '/user/';

export interface UserDocument {
  id: string;
  gedId: string;
  title: string;
  fileName: string;
  weight: number;
  contentType: string;
  idProfil: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
}
export interface CreateUserDocument {
  title: string;
  fileName: string;
  content: string;
  mime: string;
  category?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserDocumentsService {
  private get httpOptions() {
    return {
      withCredentials: true,
      observe: 'response' as 'response',
      headers: new HttpHeaders(),
      params: undefined,
    };
  }
  private basePath = environment.basePath + USER_DOCUMENTS_PATH;
  constructor(private _http: HttpClient, private _snack: SnackbarService) {}

  public async findUserDocuments(filters?: {
    title?: string;
    category?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    perPage?: number;
  }): Promise<Paginated<UserDocument>> {
    return new Promise<Paginated<UserDocument>>(async (resolve, reject) => {
      try {
        const options = this.httpOptions;
        if (filters) {
          let params = new HttpParams();
          if (filters.title) {
            params = params.set('title', filters.title);
          }
          if (filters.category) {
            params = params.set('category', filters.category);
          }
          if (filters.dateFrom) {
            params = params.set('dateFrom', filters.dateFrom);
          }
          if (filters.dateTo) {
            params = params.set('dateTo', filters.dateTo);
          }
          if (filters.page) {
            params = params.set('page', filters.page.toString());
          }
          if (filters.perPage) {
            params = params.set('perPage', filters.perPage.toString());
          }
          options.params = params;
        }
        const res = await this._http
          .get<UserDocument[]>(
            `${this.basePath}user-documents`,
            options
          )
          .toPromise();
        const documents = res.body as Array<UserDocument>;
        const paginated: Paginated<UserDocument> = {
          page: Number(res.headers.get('x-page')) || (filters?.page ?? 1),
          perpage: Number(res.headers.get('x-per-page')) || (filters?.perPage ?? documents.length),
          total: Number(res.headers.get('x-total')) || documents.length,
          objects: documents,
        };
        resolve(paginated);
      } catch (e) {
        reject(e);
      }
    });
  }

  public findOneUserDocument(id: string): Promise<UserDocument> {
    return new Promise<UserDocument>(async (resolve, reject) => {
      try {
        const res = await this._http
          .get<UserDocument>(
            `${this.basePath}user-documents/${id}`,
            this.httpOptions
          )
          .toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }
  public createUserDocument(
    document: CreateUserDocument
  ): Promise<UserDocument> {
    return new Promise<UserDocument>(async (resolve, reject) => {
      try {
        const res = await this._http
          .post<UserDocument>(
            `${this.basePath}user-documents`,
            document,
            this.httpOptions
          )
          .toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public updateUserDocument(
    id: string,
    document: UserDocument
  ): Promise<UserDocument> {
    return new Promise<UserDocument>(async (resolve, reject) => {
      try {
        const res = await this._http
          .put<UserDocument>(
            `${this.basePath}user-documents/${id}`,
            document,
            this.httpOptions
          )
          .toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }
  public deleteUserDocument(id: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http
          .delete(`${this.basePath}user-documents/${id}`, this.httpOptions)
          .toPromise();
        resolve();
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public downloadUserDocument(id: string): Promise<{
    payload: string;
    fileName: string;
    contentType: string;
  }> {
    return new Promise<{
      payload: string;
      fileName: string;
      contentType: string;
    }>(async (resolve, reject) => {
      try {
        const res = await this._http
          .get<{
            payload: string;
            fileName: string;
            contentType: string;
          }>(`${this.basePath}user-documents/${id}/download`, this.httpOptions)
          .toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }
}
